/**
 * «Здесь сейчас говорят» for the server rail.
 *
 * The rail already shouts about unread text and says nothing about voice, which
 * for a team is the more actionable of the two: you want to know where people
 * are sitting right now without opening three servers to find out. The data has
 * always been in the store (`voiceByChannel`) — nothing in the rail read it.
 *
 * Pure so the counting rules (who counts, what a folder shows) are tested
 * instead of eyeballed against a live call.
 */

/** Minimal shape of a channel — only what the count needs. */
export interface VoiceChannelRef {
  id: string;
  type: string;
}

/**
 * How many people are sitting in this server's voice channels.
 *
 * Counts me too: my own tile showing the marker is correct — I *am* in there,
 * and hiding it would make the rail disagree with the voice bar at the bottom
 * of my own sidebar. Only VOICE channels are looked at, because a text channel
 * id can never appear in `voiceByChannel` and matching on it would be a lie
 * waiting for a schema change.
 */
export function guildVoiceCount(
  channels: VoiceChannelRef[],
  voiceByChannel: Record<string, { userId: string }[]>,
): number {
  let total = 0;
  for (const channel of channels) {
    if (channel.type !== 'VOICE') continue;
    total += voiceByChannel[channel.id]?.length ?? 0;
  }
  return total;
}

/**
 * What the marker prints, or null when there is nothing to mark.
 *
 * Always a number, one included. The chip used to carry an animated equaliser
 * and add the count only from two up, which meant a lone person got a glyph and
 * no number — but at the ~10px a rail badge allows, three bars each scaling to
 * their own height are not read as bars. At any instant they are three
 * different heights, so the mark is a small asymmetric smudge that changes
 * shape ten times a second. A digit is the densest thing that stays crisp at
 * that size, and «сколько человек» is the more useful answer anyway.
 *
 * Past nine it saturates: the exact number stops mattering and three glyphs
 * don't fit a 16px chip on a 48px tile.
 */
export function voiceMarkerLabel(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count > 9 ? '9+' : String(Math.floor(count));
}
